import { zodResolver } from "@hookform/resolvers/zod/dist/zod.js"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useEffect, useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { LocationAddressDetailsForm } from "../LocationAddressDetailsForm"
import { LocationMenuDialogs } from "../types/dialogs"

import { Button } from "@/components/common/Button"
import { US_COUNTRY_ISO_CODE } from "@/constants/locations"
import { useAccountManagers } from "@/hooks/api/useAccountManagers"
import useGetSalesPerson from "@/hooks/api/useGetSalesPerson"
import useHandleCloseWithDataSave from "@/screens/Companies/edit/components/locations/hooks/useHandleCloseWithDataSave"
import { useGetCountriesAndRegions } from "@/screens/Companies/hooks/useGetCountriesAndRegions"
import {
  type AddAddressDialogProps,
  AddressCreateSchema,
  type LocationCreateFormValue,
} from "@/screens/Companies/types/dialogProps"
import type { DialogCloseAction } from "@/types/common"
import type { Option } from "@/types/form-fields"

export const AddAddressDialog: FC<AddAddressDialogProps> = ({ data, onClose, onSubmit, isCustomerCompanyType }) => {
  const { visibleManagers } = useAccountManagers({})
  const { salesPersons } = useGetSalesPerson()

  const managers = visibleManagers.map(({ name, userId }) => ({
    name,
    id: userId,
  }))

  const salesPersonOptions: Option<string>[] = useMemo(
    () =>
      salesPersons.map(({ name, externalSystemId }) => ({
        label: name ?? "",
        value: externalSystemId.toString(),
      })),
    [salesPersons]
  )

  const formValues: LocationCreateFormValue = {
    ...data,
    countryIsoCode: data.countryIsoCode || US_COUNTRY_ISO_CODE,
    address1: data.address1 || "",
    address2: data.address2 || "",
    city: data.city || "",
    name: data.name || "",
    postalCode: data.postalCode || "",
  }

  const methods = useForm<LocationCreateFormValue>({
    values: formValues,
    resolver: zodResolver(AddressCreateSchema),
    mode: "onChange",
  })
  const {
    clearErrors,
    setValue,
    watch,
    getValues,
    trigger,
    reset,
    handleSubmit,
    formState: { isValid, errors },
  } = methods

  const handleClose = useHandleCloseWithDataSave(getValues, ({ data, reason }) =>
    onClose({ data, reason: reason as DialogCloseAction })
  )

  const { countries, regions, setSelectedCountry } = useGetCountriesAndRegions()
  const countryIsoCode = watch("countryIsoCode")

  useEffect(() => {
    if (countryIsoCode) {
      setSelectedCountry(countryIsoCode)
    }
    clearErrors("regionIsoCode")
    setValue("regionIsoCode", null)
  }, [countryIsoCode, setSelectedCountry, clearErrors, setValue])

  const handleSave = async (value: LocationCreateFormValue) => {
    const isValid = await trigger()

    if (isValid) {
      onSubmit(value)
      reset()
      onClose({ data: {}, reason: "success" })
    }
  }

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      fullWidth
      PaperProps={{ "data-testid": "add-address-dialog", sx: { maxWidth: "76rem" } }}
    >
      <DialogTitle className='pl-10 font-normal text-primary'>Add Location</DialogTitle>
      <IconButton aria-label='close' onClick={handleClose} className='absolute right-4 top-4 text-primary'>
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <LocationAddressDetailsForm
            countryList={countries}
            regionList={regions}
            managers={managers}
            modalType={LocationMenuDialogs.ADD_ADDRESS}
            salesPersonOptions={salesPersonOptions}
            isCustomerCompany={isCustomerCompanyType}
          />
        </FormProvider>
      </DialogContent>
      <DialogActions data-testid='add-address-dialog-actions' className={"justify-between px-10 pb-10"}>
        <Button appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(handleSave)} disabled={!isValid || Object.keys(errors).length > 0}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
